/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { Fragment, useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { LocationOff } from '@mui/icons-material';
import { cloneDeep } from 'lodash';
import area from '@turf/area';
import transformTranslate from '@turf/transform-translate';

import { openAnimated } from '../../../redux/actions/sideDrawerActions';
import Location from './Location';
import LocationManagement from '../LocationManagement';
import Spinner from '../../shared/atoms/Spinner';
import * as S from './style';
import { useGeneralContext } from '../../../context/GeneralContext';
import { useLocationsCacheContext } from '../../../context/LocationsCacheContext';
import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { useAssetFormContext } from '../../../context/AssetFormContext';
import areaZoom from '../../../utils/areaZoom';

const CLONE_OFFSET = 10; // meters
const CLONE_ANGLE = 90; // degrees

const Locations = ({
  creationContext,
  pushPage,
  stackSize,
  modalInputText,
  popPage,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: userPermissions } = useRolePermissions();
  const { location: locationForm, setLocation } = useAssetFormContext();
  const {
    locations,
    buildings,
    assets,
    filteredLocations,
    filteredBuildings,
    filteredAssets,
    isLoading,
    refresh,
  } = useLocationsCacheContext();
  const isDrawerOpen = useSelector(store => store.sideDrawerState.isOpen);
  const [editing, setEditing] = useState(null);
  const { sideMenuForm, setSideMenuForm, isMapPage } = useGeneralContext();

  const locationEditor = useCallback(
    async (locationID, clone, buildingID) => {
      if (!locationID) {
        setEditing(null);
        setSideMenuForm(false);
      } else {
        if (locationForm) {
          setEditing(locationForm);
          setLocation(null);
          window.map.flyTo({
            center: [locationForm.lng, locationForm.lat],
            zoom: areaZoom(
              area({
                coordinates: [locationForm.coordinates],
                type: 'Polygon',
              }),
            ),
          });
          window.map.fire('flystart');
        } else {
          const foundLoc = locations.find(
            loc => loc.properties.id === locationID,
          );
          const locationToEdit = cloneDeep(foundLoc?.properties);
          locationToEdit.coordinates = !clone
            ? foundLoc?.geometry.coordinates[0]
            : transformTranslate(foundLoc, CLONE_OFFSET, CLONE_ANGLE, {
                units: 'meters',
              })?.geometry.coordinates[0];
          const buildingsToEdit = cloneDeep(
            buildings
              .filter(bld => bld.properties.parent === locationID)
              .map(bld => {
                if (clone === 'all') {
                  const shiftedBld = transformTranslate(
                    bld,
                    CLONE_OFFSET,
                    CLONE_ANGLE,
                    {
                      units: 'meters',
                    },
                  );
                  const [lng, lat] = shiftedBld.geometry.coordinates;
                  bld.properties.lng = lng;
                  bld.properties.lat = lat;
                }
                if (bld.properties.id === buildingID)
                  bld.properties.isInitialEditing = true;
                return bld.properties;
              }),
          );
          locationToEdit.buildings =
            !clone || clone === 'all' ? buildingsToEdit : [];
          const assetsToEdit = cloneDeep(
            assets
              .filter(a => a.properties.location === locationID)
              .map(a => a.properties),
          );
          locationToEdit.assets = !clone ? assetsToEdit : [];
          if (clone) delete locationToEdit.id;
          setEditing(locationToEdit);
        }
        setSideMenuForm(true);
      }
    },
    [assets, buildings, locationForm, locations, setLocation, setSideMenuForm],
  );

  useEffect(() => {
    if (isMapPage && locationForm) {
      window.map.once('idle', () => {
        setTimeout(() => locationEditor(locationForm.id), 1000);
      });
    }
  }, [isMapPage, locationEditor, locationForm]);

  useEffect(() => {
    if (!sideMenuForm && stackSize > 1) {
      setEditing(null);
      popPage();
    }
  }, [popPage, sideMenuForm, stackSize]);

  useEffect(() => {
    if (creationContext === 2) {
      pushPage(props => (
        <LocationManagement name={modalInputText} {...props} />
      ));
    } else if (editing) {
      if (stackSize === 1) {
        pushPage(props => (
          <LocationManagement
            editing={editing}
            clearEditing={() => setEditing(null)}
            {...props}
            popPage={() => {
              setEditing(null);
              props.popPage();
            }}
          />
        ));
      }
      dispatch(openAnimated());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creationContext, editing]);

  if (isLoading || refresh)
    return (
      <Stack
        flex={1}
        justifyContent="center"
        sx={{ transform: isDrawerOpen ? 'none' : 'scale(0.6)' }}
      >
        <Spinner />
      </Stack>
    );

  if (filteredLocations?.length === 0 && !isDrawerOpen) {
    return (
      <Stack flex={1} justifyContent="center" alignItems="center">
        <LocationOff fontSize="large" />
      </Stack>
    );
  }

  if (
    filteredLocations?.length === 0 &&
    locations?.length > 0 &&
    isDrawerOpen
  ) {
    return (
      <Stack flex={1} justifyContent="center" alignItems="center" spacing={2}>
        <LocationOff fontSize="large" />
        <Typography
          variant="h6"
          width="240px"
          whiteSpace="break-spaces"
          textAlign="center"
        >
          {t('components.locations.validation.no_locations_filter')}
        </Typography>
      </Stack>
    );
  }

  if (locations?.length === 0 && creationContext !== 2) {
    return (
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          marginTop: '0rem',
          marginLeft: '1rem',
        }}
      >
        {userPermissions?.map?.manage ? (
          <S.DefaultMessage>
            <div>{t('components.locations.validation.click_1')}</div>
            <S.ButtonPlusLocation>+</S.ButtonPlusLocation>
            <div>{t('components.locations.validation.click_2')}</div>
          </S.DefaultMessage>
        ) : (
          <S.DefaultMessage>
            {t('components.locations.validation.no_locations_available')}
          </S.DefaultMessage>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: 'linear-gradient(90deg, #02112d 0%, #152648 100%)',
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: 1,
      }}
    >
      {filteredLocations.map((location, index) => {
        const locationBuildings = filteredBuildings.filter(
          a => a.properties.parent === location.properties.id,
        );
        const locationAssets = filteredAssets.filter(
          a => a.properties.location === location.properties.id,
        );
        return (
          <Fragment key={location.properties.id}>
            <Location
              location={location}
              buildings={locationBuildings}
              assets={locationAssets}
              onEdit={locationEditor}
              shouldFly
            />
            {index + 1 < filteredLocations.length && (
              <Box px="10px">
                <Divider
                  sx={{
                    borderColor: theme => theme.palette.primary.main,
                    width: '100%',
                    borderBottomWidth: 1,
                    opacity: 0.5,
                  }}
                />
              </Box>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};

Locations.defaultProps = {
  creationContext: null,
  pushPage: () => ({}),
  stackSize: 0,
  modalInputText: '',
};

Locations.propTypes = {
  creationContext: PropTypes.number,
  pushPage: PropTypes.func,
  stackSize: PropTypes.number,
  modalInputText: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  popPage: PropTypes.func.isRequired,
};

export default Locations;
